import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaServices, saveSpaService, bulkImportSpaServices, exportSpaServicesToExcel, searchSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaSubCategories } from "../../components/Api/GmsSpaSubCategory";
import { getSpaCategories } from "../../components/Api/GmsSpaCategory";
import { getTaxes } from "../../components/Api/GmsTax";
import { SquarePen, Eye, Upload, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  spaService_Id: 0,
  spaServiceCode: "",
  spaSubCategory_Id: null,
  spaServiceName_En: "",
  spaServiceName_Ar: "",
  description: "",
  business_Id: 1,
  duration: 0,
  taxIncluded: false,
  status: true,
  createdBy: 1,
  createdOn: new Date().toISOString(),
  modifiedBy: null,
  modifiedOn: null,
  taxIds: [],
  discountPercentage: 0,
  amount: 0,
  cost: 0,
  netAmount: 0,
};

const emptyFilter = {
  spaServiceCode: "",
  spaSubCategory_Id: null,
  spaServiceName_En: "",
  status: null,
};

export default function SpaServicePage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaService');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaService');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaService');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaService');
  const isAdmin = isUserAdmin();

  const [spaServices, setSpaServices] = useState([]);
  const [spaSubCategories, setSpaSubCategories] = useState([]);
  const [spaCategories, setSpaCategories] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    return { ...defaultForm, status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  
  // Service name search states for filter
  const [serviceNameSearchOptions, setServiceNameSearchOptions] = useState([]);
  const [serviceNameSearchLoading, setServiceNameSearchLoading] = useState(false);
  const [serviceNameSearchTimeout, setServiceNameSearchTimeout] = useState(null);
  const [selectedServiceNameOption, setSelectedServiceNameOption] = useState(null);
  
  // Excel upload states
  const [excelFile, setExcelFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  // Load spa services
  const loadSpaServices = async () => {
    try {
      setLoading(true);
      const data = await getSpaServices(filter);
      setSpaServices(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load spa services",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load spa categories
  const loadSpaCategories = async () => {
    try {
      const data = await getSpaCategories({ status: true });
      setSpaCategories(data || []);
    } catch (error) {
      console.error("Error loading spa categories:", error);
    }
  };

  // Load spa sub categories
  const loadSpaSubCategories = async () => {
    try {
      const data = await getSpaSubCategories({ status: true });
      setSpaSubCategories(data || []);
      // Options will be updated by useEffect when categories are loaded
    } catch (error) {
      console.error("Error loading spa sub categories:", error);
    }
  };

  // Load taxes
  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
      const options = (data || []).map(tax => ({
        value: tax.tax_Id,
        label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
      }));
      setTaxOptions(options);
    } catch (error) {
      console.error("Error loading taxes:", error);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadSpaServices();
      loadSpaCategories();
      loadSpaSubCategories();
      loadTaxes();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    }
  }, [hasPermission, isViewOnly]);

  // Update subcategory options when categories or subcategories are loaded
  useEffect(() => {
    if (spaSubCategories.length > 0) {
      const options = spaSubCategories.map(subCategory => {
        if (spaCategories.length > 0) {
          const category = spaCategories.find(cat => cat.category_Id === subCategory.category_Id);
          const categoryName = category ? category.name_En : "N/A";
          const label = `${categoryName} / ${subCategory.name_En}`;
          return {
            value: subCategory.subCategory_Id,
            label: label
          };
        } else {
          // Fallback: show only subcategory name if categories aren't loaded yet
          return {
            value: subCategory.subCategory_Id,
            label: subCategory.name_En
          };
        }
      });
      setSubCategoryOptions(options);
    }
  }, [spaCategories, spaSubCategories]);

  // Update selected taxes when taxOptions are loaded and we're editing
  useEffect(() => {
    if (isEditing && taxOptions.length > 0 && form.taxIds && form.taxIds.length > 0) {
      const selectedTaxOptions = taxOptions.filter(tax =>
        form.taxIds.includes(tax.value)
      );
      setSelectedTaxes(selectedTaxOptions);
    } else if (isEditing && taxOptions.length > 0 && (!form.taxIds || form.taxIds.length === 0)) {
      setSelectedTaxes([]);
    }
  }, [taxOptions, isEditing, form.taxIds]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (serviceNameSearchTimeout) {
        clearTimeout(serviceNameSearchTimeout);
      }
    };
  }, [serviceNameSearchTimeout]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;
    
    console.log(`📝 Input Changed - ${name}:`, { value, processedValue, type });
    
    // Convert empty discount percentage to 0
    if (name === 'discountPercentage' && (value === '' || value === null || value === undefined)) {
      processedValue = 0;
    }
    
    const newForm = {
      ...form,
      [name]: processedValue
    };
    
    // Recalculate net amount when amount, discount, or taxes change
    if (name === 'amount' || name === 'discountPercentage') {
      newForm.netAmount = calculateNetAmount(
        name === 'amount' ? value : newForm.amount,
        name === 'discountPercentage' ? processedValue : newForm.discountPercentage,
        newForm.taxIds
      );
    }
    
    console.log(`✅ Form Updated - ${name}:`, { 
      oldValue: form[name], 
      newValue: processedValue,
      updatedForm: newForm 
    });
    
    setForm(newForm);
  };

  const handleSelectChange = (name, selected) => {
    console.log(`📋 Select Changed - ${name}:`, { 
      selected, 
      value: selected ? selected.value : null,
      currentFormValue: form[name]
    });
    
    setForm(prev => {
      const newForm = {
        ...prev,
        [name]: selected ? selected.value : null
      };
      
      console.log(`✅ Form Updated - ${name}:`, { 
        oldValue: prev[name], 
        newValue: selected ? selected.value : null,
        updatedForm: newForm 
      });
      
      return newForm;
    });
  };

  const handleTaxChange = (selected) => {
    setSelectedTaxes(selected || []);
    const taxIds = selected ? selected.map(tax => tax.value) : [];
    const newForm = {
      ...form,
      taxIds: taxIds,
      taxIncluded: taxIds.length > 0 // Auto-set taxIncluded based on tax selection
    };
    
    // Recalculate net amount when taxes change
    newForm.netAmount = calculateNetAmount(form.amount, form.discountPercentage, taxIds);
    
    setForm(newForm);
  };

  const resetForm = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    setForm({ ...defaultForm, status: defaultStatus });
    setIsEditing(false);
    setSelectedTaxes([]);
  };

  // Check for duplicate service code
  const checkDuplicateServiceCode = (serviceCode, excludeId = 0) => {
    return spaServices.some(service => 
      service.spaServiceCode === serviceCode && service.spaService_Id !== excludeId
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.spaServiceCode.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Service Code is required",
      });
      return;
    }

    // Check for duplicate service code
    if (checkDuplicateServiceCode(form.spaServiceCode.trim(), form.spaService_Id)) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Service Code already exists. Please use a unique code.",
      });
      return;
    }

    // Check for null or undefined specifically, not falsy values (0 is a valid ID)
    if (form.spaSubCategory_Id === null || form.spaSubCategory_Id === undefined) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Spa Sub Category is required",
      });
      return;
    }

    if (!form.spaServiceName_En.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Service Name (English) is required",
      });
      return;
    }

    try {
      setSaveLoading(true);
      const session = getUserSession();
      
      const submitData = {
        ...form,
        createdBy: session.userId,
        modifiedBy: isEditing ? session.userId : null,
        taxIds: selectedTaxes.map(tax => tax.value), // Ensure taxIds are included
        discountPercentage: form.discountPercentage || 0, // Ensure discount is 0 if empty
      };

      await saveSpaService(submitData);
      
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Spa Service ${isEditing ? 'updated' : 'created'} successfully`,
      });

      resetForm();
      loadSpaServices();
      // Stay on the Add tab for continuous service creation
    } catch (error) {
      console.error("Error saving spa service:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to ${isEditing ? 'update' : 'create'} spa service`,
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleView = (service) => {
    const subCategoryName = getSubCategoryName(service.spaSubCategory_Id);
    const taxNames = getTaxNames(service.taxIds);
    
    Swal.fire({
      title: 'Spa Service Details',
      html: `
        <div style="text-align: left;">
          <div style="margin-bottom: 15px;">
            <strong>Service Code:</strong> ${service.spaServiceCode}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Service Name (English):</strong> ${service.spaServiceName_En}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Service Name (Arabic):</strong> ${service.spaServiceName_Ar || 'N/A'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Sub Category:</strong> ${subCategoryName}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Description:</strong> ${service.description || 'N/A'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Duration:</strong> ${service.duration} minutes
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Sales Price:</strong> ${service.amount || '0.00'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Cost Price:</strong> ${service.cost || '0.00'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Net Amount:</strong> ${service.netAmount || '0.00'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Discount:</strong> ${service.discountPercentage ? service.discountPercentage + '%' : 'N/A'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Taxes:</strong> ${taxNames}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Tax Applicable:</strong> ${service.taxIncluded ? 'Yes' : 'No'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Status:</strong> <span style="color: ${service.status ? 'green' : 'red'}">${service.status ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      `,
      width: '600px',
      showCloseButton: true,
      showConfirmButton: false,
      customClass: {
        popup: 'swal-wide'
      }
    });
  };

  const handleEdit = async (service) => {
    try {
      setEditLoadingRow(service.spaService_Id);
      
      setForm({
        spaService_Id: service.spaService_Id,
        spaServiceCode: service.spaServiceCode,
        spaSubCategory_Id: service.spaSubCategory_Id,
        spaServiceName_En: service.spaServiceName_En,
        spaServiceName_Ar: service.spaServiceName_Ar || "",
        description: service.description || "",
        business_Id: service.business_Id,
        duration: service.duration,
        taxIncluded: service.taxIncluded,
        status: service.status,
        createdBy: service.createdBy,
        createdOn: service.createdOn,
        modifiedBy: service.modifiedBy,
        modifiedOn: service.modifiedOn,
        taxIds: service.taxIds || [],
        discountPercentage: service.discountPercentage || 0,
        amount: service.amount || 0,
        cost: service.cost || 0,
        netAmount: service.netAmount || 0,
      });

      if (service.taxIds && service.taxIds.length > 0) {
        const selectedTaxOptions = taxOptions.filter(tax =>
          service.taxIds.includes(tax.value)
        );
        setSelectedTaxes(selectedTaxOptions);
      } else {
        setSelectedTaxes([]);
      }

      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      console.error("Error editing spa service:", error);
    } finally {
      setEditLoadingRow(null);
    }
  };

  // Manual search function for service name filter
  const searchServiceNameManually = async (inputValue) => {
    console.log('🔍 searchServiceNameManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setServiceNameSearchOptions([]);
      return;
    }
    
    try {
      setServiceNameSearchLoading(true);
      console.log('🚀 Making API call to searchSpaServices with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchSpaServices(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(service => ({
        value: service.spaServiceName_En,
        label: `${service.spaServiceName_En || 'N/A'} — ${service.spaServiceCode || 'N/A'}`,
        service: service
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setServiceNameSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching spa services:', error);
      setServiceNameSearchOptions([]);
    } finally {
      setServiceNameSearchLoading(false);
    }
  };

  const applyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadSpaServices();
  };

  const clearFilter = () => {
    setFilter(emptyFilter);
    setSelectedServiceNameOption(null);
    setServiceNameSearchOptions([]);
    setHasAppliedFilter(false);
    setPage(1);
    loadSpaServices();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.spaServiceCode && filter.spaServiceCode.trim() !== '' && { spaServiceCode: filter.spaServiceCode }),
        ...(filter.spaSubCategory_Id !== null && filter.spaSubCategory_Id !== undefined && { spaSubCategory_Id: filter.spaSubCategory_Id }),
        ...(filter.spaServiceName_En && filter.spaServiceName_En.trim() !== '' && { spaServiceName_En: filter.spaServiceName_En }),
        ...(filter.status !== null && filter.status !== undefined && { status: filter.status })
      };
      
      await exportSpaServicesToExcel(cleanFilter);
      Swal.fire("Success", "Spa service report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa services:", error);
      Swal.fire("Error", "Failed to export spa service report", "error");
    } finally {
      setLoading(false);
    }
  };

  const getTaxNames = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return "N/A";
    const taxNames = taxIds.map(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      return tax ? tax.tax_NameEn : `Tax ID: ${taxId}`;
    });
    return taxNames.join(", ");
  };

  const getCategoryName = (subCategoryId) => {
    const subCategory = spaSubCategories.find(cat => cat.subCategory_Id === subCategoryId);
    if (!subCategory) return "N/A";
    
    const category = spaCategories.find(cat => cat.category_Id === subCategory.category_Id);
    return category ? category.name_En : "N/A";
  };

  const getSubCategoryName = (subCategoryId) => {
    const subCategory = spaSubCategories.find(cat => cat.subCategory_Id === subCategoryId);
    return subCategory ? subCategory.name_En : "N/A";
  };

  // Calculate Net Amount: Amount - Discount% + Tax%
  const calculateNetAmount = (amount, discountPercentage, taxIds) => {
    const amountValue = parseFloat(amount) || 0;
    const discountPercent = parseFloat(discountPercentage) || 0;
    
    // Calculate discount amount
    const discountAmount = (amountValue * discountPercent) / 100;
    
    // Calculate amount after discount
    const amountAfterDiscount = amountValue - discountAmount;
    
    // Calculate total tax percentage from selected taxes
    let totalTaxPercentage = 0;
    if (taxIds && taxIds.length > 0) {
      taxIds.forEach(taxId => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        if (tax) {
          totalTaxPercentage += parseFloat(tax.tax_Perc) || 0;
        }
      });
    }
    
    // Calculate tax amount
    const taxAmount = (amountAfterDiscount * totalTaxPercentage) / 100;
    
    // Net amount = Amount after discount + Tax
    const netAmount = amountAfterDiscount + taxAmount;
    
    return Math.round(netAmount * 100) / 100; // Round to 2 decimal places
  };

  // Calculate and log button disabled state for debugging
  const isButtonDisabled = useMemo(() => {
    const serviceCodeEmpty = !form.spaServiceCode.trim();
    // Check for null or undefined specifically, not falsy values (0 is a valid ID)
    const subCategoryEmpty = form.spaSubCategory_Id === null || form.spaSubCategory_Id === undefined;
    const serviceNameEmpty = !form.spaServiceName_En.trim();
    const isDuplicate = form.spaServiceCode.trim() && checkDuplicateServiceCode(form.spaServiceCode.trim(), form.spaService_Id);
    
    const disabled = saveLoading || serviceCodeEmpty || subCategoryEmpty || serviceNameEmpty || isDuplicate;
    
    // Console log for debugging
    console.log('🔍 Save Button Debug:', {
      saveLoading,
      serviceCode: form.spaServiceCode,
      serviceCodeEmpty,
      spaSubCategory_Id: form.spaSubCategory_Id,
      subCategoryEmpty,
      serviceName_En: form.spaServiceName_En,
      serviceNameEmpty,
      isDuplicate,
      disabled,
      formState: {
        spaServiceCode: form.spaServiceCode,
        spaSubCategory_Id: form.spaSubCategory_Id,
        spaServiceName_En: form.spaServiceName_En,
        spaService_Id: form.spaService_Id
      }
    });
    
    return disabled;
  }, [saveLoading, form.spaServiceCode, form.spaSubCategory_Id, form.spaServiceName_En, form.spaService_Id, spaServices]);

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = spaServices.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Excel import functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      Swal.fire("Error", "Please select a valid Excel file (.xlsx or .xls)", "error");
      return;
    }

    setExcelFile(file);
    parseExcelFile(file);
  };

  // Helper function to get value from row with case-insensitive column matching
  const getValue = (row, possibleKeys) => {
    for (const key of possibleKeys) {
      if (row.hasOwnProperty(key)) {
        return row[key];
      }
      // Case-insensitive match
      const foundKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
      if (foundKey) {
        return row[foundKey];
      }
    }
    return null;
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        console.log('📊 Excel Data Parsed:', {
          totalRows: jsonData.length,
          firstRow: jsonData[0],
          allColumnNames: jsonData.length > 0 ? Object.keys(jsonData[0]) : []
        });

        if (jsonData.length === 0) {
          Swal.fire("Error", "No data found in the Excel file", "error");
          return;
        }

        // Log all available column names
        if (jsonData.length > 0) {
          const columnNames = Object.keys(jsonData[0]);
          console.log('📋 Available Column Names:', columnNames);
          console.log('🔍 Looking for SubCategoryCode in columns:', columnNames.filter(name => 
            name.toLowerCase().includes('subcategory') || name.toLowerCase().includes('sub category')
          ));
        }

        // Check if subcategories are loaded
        if (!spaSubCategories || spaSubCategories.length === 0) {
          Swal.fire("Error", "Subcategories not loaded. Please wait and try again.", "error");
          return;
        }

        console.log('📦 Loaded SubCategories:', spaSubCategories.length, spaSubCategories.map(sc => ({
          id: sc.subCategory_Id,
          code: sc.subCategoryCode,
          name: sc.name_En
        })));

        // Create a map of subcategory codes to IDs for quick lookup
        const subCategoryCodeMap = new Map();
        spaSubCategories.forEach(subCat => {
          if (subCat.subCategoryCode) {
            // Normalize code (trim and remove spaces, case-insensitive)
            const normalizedCode = subCat.subCategoryCode.trim().replace(/\s+/g, '').toUpperCase();
            subCategoryCodeMap.set(normalizedCode, subCat.subCategory_Id);
          }
        });

        const invalidCodes = new Set();
        const rowsWithErrors = [];

        // Map Excel data to spa service format
        const mappedData = jsonData.map((row, index) => {
          console.log(`\n🔍 Processing Row ${index + 2}:`, {
            rowIndex: index + 2,
            rawRow: row,
            availableKeys: Object.keys(row)
          });

          // Extract service name from Item Name column
          const serviceName = getValue(row, ['Item Name', 'item name', 'Item Name', 'ITEM NAME']) || '';
          console.log(`  ✅ Service Name: "${serviceName}"`);
          
          // Extract rate and remove "AED" prefix
          const rateText = getValue(row, ['Rate', 'rate', 'RATE']) || '';
          const rateValue = parseFloat(rateText.toString().replace(/[^\d.]/g, '')) || 0;
          console.log(`  ✅ Rate: "${rateText}" -> ${rateValue}`);

          // Extract SubCategoryCode from Excel - try multiple variations
          const subCategoryCodeVariations = [
            'SubCategoryCode', 
            'subCategoryCode',
            'subcategorycode', 
            'Sub Category Code', 
            'sub category code', 
            'SUBCATEGORYCODE',
            'SubCategory Code',
            'Subcategory Code',
            'subcategory code',
            'SUB_CATEGORY_CODE',
            'sub_category_code'
          ];
          
          let subCategoryCode = null;
          for (const variation of subCategoryCodeVariations) {
            const value = getValue(row, [variation]);
            if (value !== null && value !== undefined && value !== '') {
              subCategoryCode = value;
              console.log(`  ✅ Found SubCategoryCode using variation "${variation}": "${subCategoryCode}"`);
              break;
            }
          }

          if (!subCategoryCode) {
            // Try direct property access
            subCategoryCode = row.SubCategoryCode || row.subCategoryCode || row['SubCategoryCode'] || row['subCategoryCode'];
            if (subCategoryCode) {
              console.log(`  ✅ Found SubCategoryCode via direct access: "${subCategoryCode}"`);
            } else {
              console.log(`  ❌ SubCategoryCode NOT FOUND in row ${index + 2}`);
              console.log(`     Available keys:`, Object.keys(row));
              console.log(`     Row data:`, row);
            }
          }

          let spaSubCategory_Id = null;

          if (subCategoryCode) {
            // Normalize the code (trim and remove spaces, case-insensitive)
            const normalizedCode = subCategoryCode.toString().trim().replace(/\s+/g, '').toUpperCase();
            console.log(`  🔄 Normalized code: "${normalizedCode}"`);
            spaSubCategory_Id = subCategoryCodeMap.get(normalizedCode);
            console.log(`  ${spaSubCategory_Id ? '✅' : '❌'} SubCategory ID lookup: ${spaSubCategory_Id || 'NOT FOUND'}`);
            
            if (!spaSubCategory_Id) {
              invalidCodes.add(subCategoryCode.toString().trim());
              rowsWithErrors.push(index + 2); // +2 because Excel rows are 1-indexed and we have a header
              console.log(`  ⚠️  Code "${subCategoryCode}" not found in subcategories map`);
            }
          } else {
            // If no subcategory code provided, show error
            rowsWithErrors.push(index + 2);
            console.log(`  ❌ No SubCategoryCode found for row ${index + 2}`);
          }

          return {
            spaService_Id: 0, // Changed from null to 0 for new records
            spaServiceCode: "", // Will be auto-generated
            spaServiceName_En: serviceName,
            spaServiceName_Ar: "",
            description: "",
            business_Id: 1,
            duration: 0,
            taxIncluded: false,
            status: true,
            createdBy: getUserSession().userId,
            createdOn: new Date().toISOString(),
            modifiedBy: null,
            modifiedOn: null,
            taxIds: [],
            discountPercentage: 0,
            amount: rateValue,
            cost: rateValue * 0.6, // 60% of rate
            netAmount: rateValue,
            spaSubCategory_Id: spaSubCategory_Id,
            subCategoryCode: subCategoryCode ? subCategoryCode.toString().trim() : null // Include code for backend lookup
          };
        }).filter(item => item.spaServiceName_En.trim() !== ''); // Filter out empty names

        if (mappedData.length === 0) {
          Swal.fire("Error", "No valid data found in the Excel file", "error");
          return;
        }

        console.log('\n📊 Mapping Summary:', {
          totalRows: mappedData.length,
          rowsWithCodes: mappedData.filter(item => item.subCategoryCode).length,
          rowsWithoutCodes: mappedData.filter(item => !item.subCategoryCode).length,
          invalidCodes: Array.from(invalidCodes),
          rowsWithErrors: rowsWithErrors
        });

        // Filter out items with NO SubCategoryCode at all (but keep items with codes even if frontend can't match them)
        // Let the backend do the final validation since it's the source of truth
        const dataWithCodes = mappedData.filter(item => {
          const hasCode = item.subCategoryCode && item.subCategoryCode.trim() !== '';
          if (!hasCode) {
            console.log('❌ Filtered out item (no code):', item);
          }
          return hasCode;
        });
        
        console.log('✅ Data with codes:', dataWithCodes.length, dataWithCodes);
        
        if (dataWithCodes.length === 0) {
          let errorMessage = "No valid data with SubCategoryCode found in the Excel file.\n\n";
          if (rowsWithErrors.length > 0) {
            errorMessage += `Rows missing SubCategoryCode: ${rowsWithErrors.slice(0, 10).join(', ')}${rowsWithErrors.length > 10 ? '...' : ''}\n`;
          }
          errorMessage += "\nPlease ensure all rows have a SubCategoryCode column.";
          
          console.error('❌ No data with codes found. Error details:', {
            mappedData,
            dataWithCodes,
            rowsWithErrors,
            invalidCodes: Array.from(invalidCodes)
          });
          
          Swal.fire({
            icon: "error",
            title: "Missing SubCategory Codes",
            text: errorMessage,
            width: '600px'
          }).then(() => {
            // Clear file input on error
            const fileInput = document.getElementById('excelFile');
            if (fileInput) {
              fileInput.value = '';
            }
            setExcelFile(null);
            setParsedData([]);
          });
          return;
        }

        // Show warning if frontend found some codes it couldn't match, but still send to backend
        if (invalidCodes.size > 0) {
          let warningMessage = `Found ${dataWithCodes.length} services with SubCategoryCode.\n\n`;
          warningMessage += `Frontend couldn't match these codes (backend will validate): ${Array.from(invalidCodes).slice(0, 5).join(', ')}${invalidCodes.size > 5 ? '...' : ''}\n`;
          warningMessage += "\nBackend will perform final validation.";
          
          Swal.fire({
            icon: "info",
            title: "SubCategory Code Validation",
            text: warningMessage,
            width: '600px'
          });
        }

        // Send all data with codes to backend - let backend do the final validation
        // Set spaSubCategory_Id to null if frontend couldn't match, backend will look it up
        const dataToSend = dataWithCodes.map(item => ({
          ...item,
          spaSubCategory_Id: item.spaSubCategory_Id || null // Backend will look it up using code
        }));

        setParsedData(dataToSend);
        if (invalidCodes.size === 0) {
          Swal.fire("Success", `Found ${dataToSend.length} spa services to import`, "success");
        }
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        Swal.fire("Error", "Failed to parse Excel file", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkImport = async () => {
    if (parsedData.length === 0) {
      Swal.fire("Error", "No data to import", "error");
      return;
    }

    try {
      setImportLoading(true);
      setImportProgress({ current: 0, total: parsedData.length });

      // Log the data being sent
      console.log('📤 Sending data to backend:', {
        count: parsedData.length,
        firstItem: parsedData[0],
        allItems: parsedData
      });

      const result = await bulkImportSpaServices(parsedData);

      // Check if the response has ids (success) or error message
      if (result && result.ids) {
        let message = result.message || `Successfully imported ${parsedData.length} spa services!`;
        
        // Show warning if there are invalid subcategory codes from backend
        if (result.invalidSubCategoryCodes && result.invalidSubCategoryCodes.length > 0) {
          const invalidCodes = Array.isArray(result.invalidSubCategoryCodes) 
            ? result.invalidSubCategoryCodes.join(', ')
            : result.invalidSubCategoryCodes;
          
          Swal.fire({
            icon: "warning",
            title: "Import Completed with Warnings",
            html: `
              <p>${message}</p>
              <p><strong>Invalid SubCategoryCodes found:</strong> ${invalidCodes}</p>
              <p>These services were imported with default subcategory.</p>
            `,
            width: '600px'
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Import Successful",
            text: message,
          });
        }
        
        // Clear the data and reload the list
        setParsedData([]);
        setExcelFile(null);
        loadSpaServices();
      } else {
        Swal.fire({
          icon: "error",
          title: "Import Failed",
          text: result.message || "Failed to import spa services",
        }).then(() => {
          // Clear file input on error
          const fileInput = document.getElementById('excelFile');
          if (fileInput) {
            fileInput.value = '';
          }
          setExcelFile(null);
          setParsedData([]);
        });
      }
    } catch (error) {
      console.error("❌ Error importing spa services:", error);
      console.error("❌ Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        requestData: parsedData
      });
      
      // Extract error message from response
      let errorMessage = "Failed to import spa services. Please try again.";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Import Error",
        html: `<p>${errorMessage}</p><p style="font-size: 12px; color: #666;">Status: ${error.response?.status || 'Unknown'}</p>`,
        width: '600px'
      }).then(() => {
        // Clear file input on error
        const fileInput = document.getElementById('excelFile');
        if (fileInput) {
          fileInput.value = '';
        }
        setExcelFile(null);
        setParsedData([]);
      });
    } finally {
      setImportLoading(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  const clearImportData = () => {
    setParsedData([]);
    setExcelFile(null);
    setImportProgress({ current: 0, total: 0 });
  };

  // Pagination component (same style as CustomerPage)
  const PaginationComponent = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{" "}
          {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="pagination-wrapper">
          <Pagination
            className="ant-pagination"
            current={page}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showTitle={false}
          />
        </div>
      </div>
    );
  };

  // Status options for filter
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Service Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Spa Service</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Spa Services List</span>
                    </NavLink>
                  </NavItem>
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "3" ? "bold" : "normal",
                        color: activeTab === "3" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                      >
                        <Upload size={16} />
                        <span>Excel Import</span>
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={handleSubmit}>
                        {/* Basic Information */}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="spaServiceCode">Service Code <span className="text-danger">*</span></Label>
                              <Input
                                id="spaServiceCode"
                                name="spaServiceCode"
                                type="text"
                                value={form.spaServiceCode}
                                onChange={handleInputChange}
                                placeholder="Enter service code"
                                required
                                className={form.spaServiceCode.trim() && checkDuplicateServiceCode(form.spaServiceCode.trim(), form.spaService_Id) ? 'is-invalid' : ''}
                              />
                              {form.spaServiceCode.trim() && checkDuplicateServiceCode(form.spaServiceCode.trim(), form.spaService_Id) && (
                                <div className="invalid-feedback">
                                  Service Code already exists. Please use a unique code.
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="spaSubCategory_Id">Spa Sub Category <span className="text-danger">*</span></Label>
                              <Select
                                id="spaSubCategory_Id"
                                value={subCategoryOptions.find(option => option.value === form.spaSubCategory_Id) || null}
                                onChange={(selected) => handleSelectChange('spaSubCategory_Id', selected)}
                                options={subCategoryOptions}
                                placeholder="Select sub category"
                                styles={selectStyles}
                                isClearable
                                required
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="spaServiceName_En">Service Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                id="spaServiceName_En"
                                name="spaServiceName_En"
                                type="text"
                                value={form.spaServiceName_En}
                                onChange={handleInputChange}
                                placeholder="Enter service name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="spaServiceName_Ar">Service Name (Arabic)</Label>
                              <Input
                                id="spaServiceName_Ar"
                                name="spaServiceName_Ar"
                                type="text"
                                value={form.spaServiceName_Ar}
                                onChange={handleInputChange}
                                placeholder="Enter service name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="description">Description</Label>
                              <Input
                                id="description"
                                name="description"
                                type="textarea"
                                value={form.description}
                                onChange={handleInputChange}
                                placeholder="Enter description"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="duration">Duration (minutes)</Label>
                              <Input
                                id="duration"
                                name="duration"
                                type="number"
                                value={form.duration}
                                onChange={handleInputChange}
                                placeholder="Enter duration in minutes"
                                min="0"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="amount">Sales Price</Label>
                              <Input
                                id="amount"
                                name="amount"
                                type="number"
                                value={form.amount}
                                onChange={handleInputChange}
                                placeholder="Enter sales price"
                                min="0"
                                step="0.01"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="discountPercentage">Discount Percentage (%)</Label>
                              <Input
                                id="discountPercentage"
                                name="discountPercentage"
                                type="number"
                                value={form.discountPercentage || 0}
                                onChange={handleInputChange}
                                placeholder="Enter discount percentage"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="taxIds">Taxes</Label>
                              <Select
                                id="taxIds"
                                value={selectedTaxes}
                                onChange={handleTaxChange}
                                options={taxOptions}
                                placeholder="Select Taxes"
                                styles={selectStyles}
                                isMulti
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="netAmount">Net Amount</Label>
                              <Input
                                id="netAmount"
                                name="netAmount"
                                type="number"
                                value={form.netAmount}
                                readOnly
                                className="bg-light"
                                placeholder="Calculated automatically"
                                step="0.01"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="cost">Cost Price</Label>
                              <Input
                                id="cost"
                                name="cost"
                                type="number"
                                value={form.cost}
                                onChange={handleInputChange}
                                placeholder="Enter cost price"
                                min="0"
                                step="0.01"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        
                        {hasDeletePermission && (
                          <Row>
                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <div className="form-check form-switch d-flex align-items-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="status"
                                    checked={form.status}
                                    onChange={(e) => {
                                      setForm(prev => ({
                                        ...prev,
                                        status: e.target.checked
                                      }));
                                    }}
                                  />
                                  <label className="form-check-label mb-0" htmlFor="status" style={{ marginLeft: '0.25rem' }}>
                                    {form.status ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={isButtonDisabled}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Spa Service" : "Save Spa Service"}
                              </Button>
                              {isEditing && (
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={resetForm}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="3">
                    <div>
                      <Row>
                        <Col xs={12}>
                          <Card>
                            <CardBody>
                              <h5 className="card-title mb-4">Excel Import</h5>
                              <p className="text-muted mb-4">
                                Upload an Excel file to bulk import spa services. The Excel file should contain columns such as{" "}
                                <strong>ServiceCode</strong>, <strong>ServiceName</strong>, <strong>Price</strong>, <strong>Cost</strong>, and{" "}
                                <strong>SubCategoryCode</strong>.
                              </p>
                              
                              <Row>
                                <Col md={6}>
                                  <FormGroup>
                                    <Label for="excelFile">Select Excel File</Label>
                                    <Input
                                      id="excelFile"
                                      type="file"
                                      accept=".xlsx,.xls"
                                      onChange={handleFileUpload}
                                      className="form-control"
                                    />
                                    <small className="form-text text-muted">
                                      Supported formats: .xlsx, .xls
                                    </small>
                                  </FormGroup>
                                </Col>
                              </Row>

                              {parsedData.length > 0 && (
                                <div className="mb-4">
                                  <h6>Preview ({parsedData.length} services found):</h6>
                                  <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Table className="table table-bordered table-striped table-sm">
                                      <thead className="table-dark">
                                        <tr>
                                          <th>#</th>
                                          <th>Service Name</th>
                                          <th>Sub Category</th>
                                          <th>Rate</th>
                                          <th>Amount</th>
                                          <th>Cost (60%)</th>
                                          <th>Net Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {parsedData.slice(0, 10).map((service, index) => (
                                          <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{service.spaServiceName_En}</td>
                                            <td>{getSubCategoryName(service.spaSubCategory_Id)}</td>
                                            <td>{service.amount}</td>
                                            <td>{service.amount}</td>
                                            <td>{service.cost}</td>
                                            <td>{service.netAmount}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              )}

                              {parsedData.length > 10 && (
                                <div className="mt-4 mb-4">
                                  <p className="text-muted mb-0">
                                    Showing first 10 services. Total: {parsedData.length} services
                                  </p>
                                </div>
                              )}

                              <div className="d-flex gap-2">
                                <Button
                                  color="success"
                                  onClick={handleBulkImport}
                                  disabled={parsedData.length === 0 || importLoading}
                                  className="waves-effect waves-light"
                                >
                                  {importLoading ? (
                                    <>
                                      <ClipLoader size={16} color="#ffffff" className="me-2" />
                                      Importing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bx bx-upload me-2"></i>
                                      Import {parsedData.length} Services
                                    </>
                                  )}
                                </Button>
                                
                                {parsedData.length > 0 && (
                                  <Button
                                    color="secondary"
                                    onClick={clearImportData}
                                    disabled={importLoading}
                                    className="waves-effect waves-light"
                                  >
                                    Clear
                                  </Button>
                                )}
                              </div>

                              {importProgress.total > 0 && (
                                <div className="mt-3">
                                  <div className="progress">
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                                    >
                                      {importProgress.current} / {importProgress.total}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Spa Services</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label for="filterServiceCode">Service Code</Label>
                                <Input
                                  id="filterServiceCode"
                                  name="spaServiceCode"
                                  type="text"
                                  value={filter.spaServiceCode}
                                  onChange={(e) => setFilter(prev => ({ ...prev, spaServiceCode: e.target.value }))}
                                  placeholder="Enter service code"
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterSubCategory">Sub Category</Label>
                                <Select
                                  id="filterSubCategory"
                                  value={subCategoryOptions.find(option => option.value === filter.spaSubCategory_Id)}
                                  onChange={(selected) => setFilter(prev => ({ ...prev, spaSubCategory_Id: selected ? selected.value : null }))}
                                  options={subCategoryOptions}
                                  placeholder="Select sub category"
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterServiceName">Service Name</Label>
                                <Select
                                  id="filterServiceName"
                                  value={selectedServiceNameOption}
                                  onChange={(option) => {
                                    setSelectedServiceNameOption(option);
                                    setFilter(prev => ({ ...prev, spaServiceName_En: option?.value || "" }));
                                  }}
                                  options={serviceNameSearchOptions}
                                  placeholder="Type at least 3 characters to search services..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={serviceNameSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching services..."}
                                  onInputChange={(inputValue) => {
                                    console.log('🔤 Filter Input changed:', inputValue);
                                    
                                    // Clear previous timeout
                                    if (serviceNameSearchTimeout) {
                                      clearTimeout(serviceNameSearchTimeout);
                                    }
                                    
                                    // Set new timeout
                                    const timeout = setTimeout(() => {
                                      searchServiceNameManually(inputValue);
                                    }, 300);
                                    setServiceNameSearchTimeout(timeout);
                                    
                                    return inputValue;
                                  }}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  id="filterStatus"
                                  value={statusOptions.find(option => option.value === filter.status)}
                                  onChange={(selected) => setFilter(prev => ({ ...prev, status: selected ? selected.value : null }))}
                                  options={statusOptions}
                                  placeholder="Select status"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={applyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={clearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Spa Services List
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => setActiveTab("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Spa Service
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading spa services...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="12%">Service Code</th>
                                      <th width="20%">Service Name</th>
                                      <th width="12%">Category</th>
                                      <th width="12%">Sub Category</th>
                                      <th width="8%">Duration</th>
                                      <th width="10%">Sales Price</th>
                                      <th width="10%">Net Amount</th>
                                      <th width="11%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {currentData.length > 0 ? (
                                      currentData.map((service, index) => (
                                        <tr key={service.spaService_Id}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{service.spaServiceCode}</td>
                                          <td>{service.spaServiceName_En}</td>
                                          <td>{getCategoryName(service.spaSubCategory_Id)}</td>
                                          <td>{getSubCategoryName(service.spaSubCategory_Id)}</td>
                                          <td>{service.duration} min</td>
                                          <td>{service.amount || "0.00"}</td>
                                          <td>{service.netAmount || "0.00"}</td>
                                          <td>
                                            <div className="d-flex gap-1">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(service)}
                                                className="waves-effect waves-light"
                                                title="View Details"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(service)}
                                                  disabled={editLoadingRow === service.spaService_Id}
                                                  className="waves-effect waves-light"
                                                  title="Edit Service"
                                                >
                                                  {editLoadingRow === service.spaService_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="9" className="text-center">
                                          {hasAppliedFilter ? "No spa services found matching the filter criteria." : "No spa services available. Please add some spa services first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalCount > 0 && <PaginationComponent />}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
